import gql from 'graphql-tag'
import { ASSET_FIELDS, ITEM_FIELDS, ITEM_SETTINGS_FIELDS } from 'src/lib/queries/fields'
import { apolloProvider } from 'boot/apollo'
import { MutationOptions } from 'apollo-client'
import { GeneralStatusEnum, ItemDataObject, ItemTypeEnum } from 'able-lib'

export const QUERY_GET_LESSON = gql`
  ${ITEM_FIELDS}
  query getLesson($lessonId: Int!) {
    lesson: item_by_pk(id: $lessonId) {
      ...itemFields
      items(where: {type: {_nin: [LESSON, LESSONPLAN]}, status: {_nin: [ARCHIVED, DELETED]}}, order_by: {order: asc}) {
        ...itemFields
      }
      originalItem: item {
        ...itemFields
        items(where: {type: {_nin: [LESSON, LESSONPLAN]}, status: {_nin: [ARCHIVED, DELETED]}}, order_by: {order: asc}) {
          ...itemFields
        }
      }
    }
  }
`

export const SUBSCRIPTION_LESSON_RECOGNITIONS = gql`
subscription MyQuery($userIds: [Int!], $lessonId: Int) {
      user(where: {id: {_in: $userIds}}) {
        id
        scores(where: {context_id: {_eq: $lessonId}}) {
          id
          score
          type
        }
        user_awards {
          id
          award_id
          created_at
          award {
            name
            comment
          }
        }
      }
    }
`

export const QUERY_GET_UPCOMING_LESSON = gql`
  ${ITEM_FIELDS}
  query upcomingLesson(
    $startDateTime: timestamptz!,
    $endDateTime: timestamptz!,
    $userId: Int!
  ) {
    lessons: item(
      where: {
        _and: {
          start_datetime: { _gt: $startDateTime },
          end_datetime: { _lt: $endDateTime },
          item_users: {
            user_id: { _eq: $userId }
          },
          type: { _eq: LESSON },
          status: { _eq: PUBLISHED }
        }
      }
    ) {
      ...itemFields
      items {
        ...itemFields
      }
    }
  }
`

export const QUERY_DELETE_LESSON = gql`
  mutation deleteLesson($itemId: Int!, $contentId: Int, $itemMaterials: [Int!], $contentMaterialIds: [Int!], $assetMaterialIds: [Int!]) {
    delete_material_item_user: delete_item_user(where: {item_id:{_in: $itemMaterials}}){
      affected_rows
    }
    delete_item_user(where: {item_id:{_eq: $itemId}}){
      affected_rows
    }
    delete_material_item_group: delete_item_group(where: {item_id:{_in: $itemMaterials}}){
      affected_rows
    }
    delete_item_group(where: {item_id:{_eq: $itemId}}){
      affected_rows
    }
    delete_material_item_setting: delete_item_setting(where: {item_id:{_in: $itemMaterials}}){
      affected_rows
    }
    delete_item_setting(where: {item_id:{_eq: $itemId}}){
      affected_rows
    }
    delete_material_content_asset: delete_content_asset(where: {content_id:{_in: $contentMaterialIds}}){
      affected_rows
    }
    delete_content_asset(where: {content_id:{_eq: $contentId}}){
      affected_rows
    }
    delete_material_item: delete_item(where: {id:{_in: $itemMaterials}}){
      affected_rows
    }
    delete_item(where: {id: {_eq: $itemId}}){
      affected_rows
    }
    delete_material_content: delete_content(where: {id:{_in: $contentMaterialIds}}){
      affected_rows
    }
    delete_content(where: {id:{_eq: $contentId}}){
      affected_rows
    }
    delete_material_asset: delete_asset(where: {id:{_in: $assetMaterialIds}}){
      affected_rows
    }
  }
`

export const QUERY_GET_MATERIALS = gql`
  ${ASSET_FIELDS}
  query getMaterials {
    materials: asset(where: {is_shared: {_eq: true}}) {
      ...assetFields
    }
  }
`

export const QUERY_SUBS_LESSON_SETTINGS = gql`
  ${ITEM_SETTINGS_FIELDS}
  subscription subscribeLessonSetting($lessonId: Int!) {
    lessonSettings: item_setting(where: {item_id: {_eq: $lessonId}}) {
      ...itemSettingFields
    }
  }
`

export const QUERY_UPDATE_LESSON_SETTINGS = gql`
  mutation ($objects: [item_setting_insert_input!]!, $removeSetting: [item_setting_bool_exp]) {
    delete_item_setting(where: {_or: $removeSetting}) {
      affected_rows
    }
    insert_item_setting(objects: $objects, on_conflict: {constraint: item_setting_pkey, update_columns: [value]}) {
      affected_rows
    }
  }
`

export const QUERY_REMOVE_LESSON_SETTINGS = gql`
  mutation ($removeSettings: [item_setting_bool_exp]) {
    delete_item_setting(where: {_or: $removeSettings}) {
      affected_rows
    }
  }
`

export const MUTATION_UPSERT_ITEM = gql`
  ${ITEM_FIELDS}
  mutation upsertItem($itemId: Int!, $remainingItems: [Int!]!, $remainingContent: [Int!]!, $remainingAssets: [Int!]!, $remainingParticipatingUsers: [Int!]!, $remainingMaterialUsers: [Int!]!, $itemDataObject: item_insert_input!, $itemConflictClause: item_on_conflict) {
    delete_item(where: {id: {_nin: $remainingItems}, type: {_nin: [LESSON, LESSONPLAN]}, item: {id: {_eq: $itemId}}}) {
      affected_rows
    }
    delete_content_asset(where: {asset_id: {_nin: $remainingAssets}, content: {items: {type: {_nin: [LESSON, LESSONPLAN]}, item: {id: {_eq: $itemId}}}}}) {
      affected_rows
    }
    delete_content(where: {id: {_nin: $remainingContent}, items: {type: {_nin: [LESSON, LESSONPLAN]}, item: {id: {_eq: $itemId}}}}) {
      affected_rows
    }
    delete_asset(where: {id: {_nin: $remainingAssets}, is_shared: {_eq: false}, content_assets: {content: {items: {type: {_nin: [LESSON, LESSONPLAN]}, item: {id: {_eq: $itemId}}}}}}) {
      affected_rows
    }
    delete_participating_item_user: delete_item_user(where: {item_id: {_eq: $itemId}, user_id: {_nin: $remainingParticipatingUsers}}) {
      affected_rows
    }
    delete_activity_user: delete_item_user(where: {item: {type: {_nin: [LESSON, LESSONPLAN]}, item: {id: {_eq: $itemId}}}, user_id: {_nin: $remainingMaterialUsers}}) {
      affected_rows
    }
    updatedItem: insert_item_one(object: $itemDataObject, on_conflict: $itemConflictClause) {
      ...itemFields
      items(where: {type: {_nin: [LESSON, LESSONPLAN]}, status: {_nin: [ARCHIVED, DELETED]}}, order_by: {order: asc}) {
        ...itemFields
      }
      originalItem: item {
        items(where: {type: {_nin: [LESSON, LESSONPLAN]}, status: {_nin: [ARCHIVED, DELETED]}}, order_by: {order: asc}) {
          ...itemFields
        }
        ...itemFields
      }
    }
  }
`

export const MUTATION_INSERT_ITEM = gql`
  ${ITEM_FIELDS}
  mutation insertItem($lessonDataObjects: item_insert_input!, $lessonConflictClause: item_on_conflict) {
    newItem: insert_item_one(object: $lessonDataObjects, on_conflict: $lessonConflictClause) {
      ...itemFields
      items(where: {type: {_nin: [LESSON, LESSONPLAN]}, status: {_nin: [ARCHIVED, DELETED]}}, order_by: {order: asc}) {
        ...itemFields
      }
      originalItem: item {
        items(where: {type: {_nin: [LESSON, LESSONPLAN]}, status: {_nin: [ARCHIVED, DELETED]}}, order_by: {order: asc}) {
          ...itemFields
        }
        ...itemFields
      }
    }
  }
`

export const QUERY_GET_LESSON_PLANS = gql`
  ${ITEM_FIELDS}
  query get_lesson_plans($lessonPlanLevelId: Int, $lessonPlanSubjectId: Int) {
    lessonPlans: item(where: {type: {_eq: LESSONPLAN}, status: {_nin: [ARCHIVED, DELETED]}, item_template_lessonplan: {level_id: {_eq: $lessonPlanLevelId}, subject_id: {_eq: $lessonPlanSubjectId}}}, order_by: {item_template_lessonplan: {number: asc, lesson: asc, level_id: asc, subject_id: asc}}) {
      ...itemFields
      items(where: {type: {_nin: [LESSON, LESSONPLAN]}, status: {_nin: [ARCHIVED, DELETED]}}) {
        ...itemFields
      }
    }
  }
`

export const QUERY_ARCHIVE_ITEM = gql`
  mutation archive_item($itemId: Int!) {
    update_item_by_pk(pk_columns:{id: $itemId}, _set: {status: ARCHIVED}) {
      id
    }
  }
`

export const DELETE_ITEM = gql`
  mutation archive_item($itemId: Int!) {
    update_item_by_pk(pk_columns:{id: $itemId}, _set: {status: DELETE}) {
      id
    }
  }
`

export function insertLessonPlans (lessons: Array<{
  number: string;
  lesson: string;
  objective: string;
  assessment: string;
  platform: string;
  resources: string;
}>, levelId: number, subjectId: number, year: number) {
  const data: Array<ItemDataObject> = []

  for (const lesson of lessons) {
    data.push({
      order: '1',
      status: GeneralStatusEnum.PUBLISHED,
      type: ItemTypeEnum.LESSONPLAN,
      item_template_lessonplan: {
        data: {
          ...lesson,
          enhancement: '',
          level_id: levelId,
          status: GeneralStatusEnum.PUBLISHED,
          subject_id: subjectId,
          year
        }
      }
    })
  }

  const options: MutationOptions = {
    mutation: gql`
      mutation insertLessonPlans($data: [item_insert_input!]!) {
        lessonPlans: insert_item(objects: $data) {
          affected_rows
          returning {
            id
          }
        }
      }
    `,
    variables: {
      data
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}
